// import external libraries

import Vue from 'vue';
// noinspection ES6UnusedImports
import _ from 'lodash';
import Buefy from 'buefy';
import { Fragment } from 'vue-frag'
import * as Sentry from '@sentry/vue';
import { Integrations as TracingIntegration } from '@sentry/tracing';
import { Dedupe as DedupeIntegration } from '@sentry/integrations';
import VueIntercom from 'vue-intercom';
import VueMq from 'vue-mq';
import AutoRotate from 'vue-jpeg-auto-rotation';
import VueConfetti from 'vue-confetti';

import { FontAwesomeIcon, FontAwesomeLayers } from '@fortawesome/vue-fontawesome';
// import internal Libraries
// noinspection ES6UnusedImports

import api from './api'; // eslint-disable-line no-unused-vars
import App from './App.vue';
import breadcrumbs from '@/plugins/breadcrumbs/breadcrumbs';
import KeepScrollPosition from '@/plugins/navigation/keep_scroll_position';
import Avatar from '@/plugins/avatar/Avatar.vue';
import FormatCurrency from '@/app/shared_components/currency/FormatCurrency.vue';
import buttons from '@/app/shared_components/buttons/buttons.js';
import i18n from '@/i18n/index';
import responsive_mixin from '@/app/util/responsive_mixin';
import router from '@/router/index';
import { Auth0Plugin } from '@/auth';
import store from '@/store/index';
import toasts from '@/app/util/toasts';
import sequentialPromise from '@/app/util/synchronous_promise';
import VPopper from '@/app/shared_components/tooltips/VPopper.vue';
import TitleSmall from '@/app/shared_components/titles/TitleSmall.vue';
import Modal from '@/app/shared_components/modals/TheModal.vue';
import truncate from '@/plugins/truncate';
// import Style Sheets
import 'nprogress/nprogress.css';
import '@/assets/css/vue-popper.css';
import '@/assets/images/fontawesome.js';

// import constants
import { ZIGGU_API_URL } from '@/app/util/urls';

// Initialize Sentry
if (!import.meta.env.IS_LOCAL) {
  Vue.config.productionTip = false;
  Vue.config.devtools = false;
  Sentry.init({
    Vue,
    dsn: 'https://be16e9a319304d88b103e2300a4f669f@sentry.io/1190623',
    release: `${import.meta.env.RELEASE_VERSION}`,
    environment: `${import.meta.env.VITE_ENV}`,
    ignoreErrors: [
      'TypeError: NetworkError when attempting to fetch resource.',
      'Non-Error exception captured',
    ],
    integrations: [
      new DedupeIntegration(),
      new TracingIntegration.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        tracingOrigins: [`${ZIGGU_API_URL.replace('https://', '')}`],
      }),
    ],
    tracesSampleRate: 0.25, // percentage
    normalizeDepth: 8,
    // prevent 401 error logs (expired sessions, see api/index.js) being sent to Sentry
    beforeSend(event, hint) {
      const isUnauthorizedError = hint
        && hint.originalException
        && hint.originalException.response
        && hint.originalException.response.status
        && hint.originalException.response.status === 401;
      const isSentryDiscard = hint
        && hint.originalException
        && hint.originalException.sentry_discard
        && hint.originalException.sentry_discard === true;
      const isNonErrorException = hint
        && hint.originalException
        && hint.originalException.error
        && event
        && event.message
        && event.message.startsWith('Non-Error exception captured');
      const isChunkError = (event.message && event.message.startsWith('Loading chunk '))
        || (event.name && event.name === 'ChunkLoadError');

      if (isUnauthorizedError || isSentryDiscard || isChunkError) {
        return null;
      }

      // Errors from Auth0 extends original Error class and cause issues with Sentry
      if (isNonErrorException) {
        Sentry.withScope((scope) => {
          scope.setExtra('nonErrorException', true);
          Sentry.captureException(hint.originalException.error);
        });
        return null;
      }

      return event;
    },
  });
} else {
  Vue.config.productionTip = true;
  Vue.config.devtools = true;
  Vue.config.performance = true;
}

Vue.use(Auth0Plugin, {
  domain: import.meta.env.VITE_AUTH0_DOMAIN,
  client_id: import.meta.env.VITE_AUTH0_APP_CLIENT_ID,
  audience: import.meta.env.VITE_AUTH0_API_AUDIENCE,
  onRedirectCallback: (appState) => {
    const path = _.get(appState, 'targetUrl', window.location.pathname);
    const query = _.get(appState, 'params', {});

    router.push({ path, query });
  },
});

window._ = _;
Vue.prototype._ = _; // not compatible with Vue 3
Vue.prototype.sequentialPromise = sequentialPromise;

Vue.use(Buefy, {
  defaultIconPack: 'fas',
});

Vue.use(VueConfetti);
Vue.mixin(responsive_mixin);
Vue.mixin(toasts);

Vue.component('Avatar', Avatar);
Vue.component('FormatCurrency', FormatCurrency);
Vue.component('FontAwesomeIcon', FontAwesomeIcon);
Vue.component('FontAwesomeLayers', FontAwesomeLayers);
Vue.component('Modal', Modal);
Vue.component('VPopper', VPopper);
Vue.component('TitleSmall', TitleSmall);
Vue.use(AutoRotate);
Vue.use(breadcrumbs);
Vue.use(KeepScrollPosition);
Vue.use(buttons);
Vue.use(VueIntercom, { appId: import.meta.env.VITE_INTERCOM_ID });
Vue.use(VueMq, {
  breakpoints: {
    sm: 768,
    md: 1025,
    lg: 1440,
    xl: Infinity,
  },
});
// necessary for vue-lite-youtube-embed package (Vue 2 only)
Vue.component('Fragment', Fragment)

const truncatePlugin = {
  install () {
    Vue.truncate = truncate
    Vue.prototype.$truncate = truncate
  }
}
Vue.use(truncatePlugin)

new Vue({
  store,
  router,
  i18n,
  render: (h) => h(App),
}).$mount('#app');
